/**
 * @file `CmdkPalette` — the admin ⌘K command palette overlay. A modal
 *       list of pages + shell actions, filtered as the operator types,
 *       driven by arrow keys + Enter. Ported from the design
 *       source-of-truth (design/admin/screens/learners/learner-detail.jsx
 *       §CmdK) and generalised to the shell's panel set.
 * @module @labcraft/admin/ui/kernel/cmdk-palette
 *
 * The component is descriptor-driven via {@link cmdk_view}; dispatch is
 * delegated to the shell through `onNavigate` / `onAction` so the
 * palette holds no domain knowledge of what an action does.
 */

import { Fragment, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import {
  buildCatalog,
  clampIndex,
  filterCommands,
  groupCommands,
  type CmdkCommand,
  type CmdkPage,
} from './cmdk_view.ts'

/**
 * Props for {@link CmdkPalette}.
 *
 * @property open - Whether the overlay is mounted/visible.
 * @property pages - The shell's panel destinations (label + href).
 * @property onClose - Dismiss the palette (Esc, backdrop, post-dispatch).
 * @property onNavigate - Navigate to a page command's href.
 * @property onAction - Run a shell action command, keyed by its id.
 */
export type CmdkPaletteProps = {
  open: boolean
  pages: readonly CmdkPage[]
  onClose: () => void
  onNavigate: (href: string) => void
  onAction: (id: string) => void
}

/**
 * The topbar "Jump to…" trigger that opens the palette. Mounted in the
 * shared `TopNav`'s `trailing` slot by the admin shell.
 *
 * @param props.onOpen - Open the palette.
 * @returns The trigger button.
 */
export function CmdkTrigger({ onOpen }: { onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      className="cmdk-trigger"
      onClick={onOpen}
      aria-label="Open command palette">
      <svg
        width={13}
        height={13}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round">
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5L13.5 13.5" />
      </svg>
      <span className="cmdk-trigger__label">Jump to…</span>
      <span className="cmdk-trigger__hint">
        <span className="kbd">⌘</span>
        <span className="kbd">K</span>
      </span>
    </button>
  )
}

/** Inline glyph for a command row, by kind. */
function CmdkIcon({ kind }: { kind: CmdkCommand['kind'] }): JSX.Element {
  const common = {
    width: 13,
    height: 13,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  return kind === 'page' ? (
    <svg {...common}>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
      <path d="M2.5 6h11" />
    </svg>
  ) : (
    <svg {...common}>
      <path d="M5 3l7 5-7 5z" />
    </svg>
  )
}

/**
 * The command palette overlay. Returns `null` when closed.
 *
 * @param props - See {@link CmdkPaletteProps}.
 * @returns The palette element, or `null` when `open` is false.
 */
export function CmdkPalette(props: CmdkPaletteProps): JSX.Element | null {
  const { open, pages, onClose, onNavigate, onAction } = props
  const [query, setQuery] = useState('')
  const [sel, setSel] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const catalog = useMemo(() => buildCatalog(pages), [pages])
  const flat = useMemo(() => filterCommands(catalog, query), [catalog, query])
  const groups = useMemo(() => groupCommands(flat), [flat])

  // Reset + focus on each open.
  useEffect(() => {
    if (!open) return
    setQuery('')
    setSel(0)
    const id = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => {
      window.clearTimeout(id)
    }
  }, [open])

  // Keep the selection in range as the filtered list shrinks.
  useEffect(() => {
    setSel(s => clampIndex(s, flat.length))
  }, [flat.length])

  // Arrow / Enter / Esc navigation while open.
  useEffect(() => {
    if (!open) return undefined
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSel(s => clampIndex(s + 1, flat.length))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSel(s => clampIndex(s - 1, flat.length))
        return
      }
      if (e.key === 'Enter') {
        const cur = flat[sel]
        if (!cur) return
        dispatch(cur)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [open, flat, sel, onClose])

  /** Fire the command's effect, then close. */
  function dispatch(cmd: CmdkCommand): void {
    if (cmd.kind === 'page' && cmd.href !== undefined) onNavigate(cmd.href)
    else if (cmd.kind === 'action') onAction(cmd.id)
    onClose()
  }

  if (!open) return null

  let idx = -1
  return (
    <div className="cmdk-backdrop" onClick={onClose}>
      <div
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={e => {
          e.stopPropagation()
        }}>
        <input
          ref={inputRef}
          className="cmdk__input"
          placeholder="jump to a page, run an action…"
          value={query}
          onChange={e => {
            setQuery(e.target.value)
            setSel(0)
          }}
        />
        <div className="cmdk__list">
          {groups.map(group => (
            <Fragment key={group.heading}>
              <div className="cmdk__group">{group.heading}</div>
              {group.commands.map(cmd => {
                idx += 1
                const i = idx
                return (
                  <div
                    key={cmd.id}
                    className="cmdk__item"
                    aria-selected={sel === i}
                    onMouseEnter={() => {
                      setSel(i)
                    }}
                    onClick={() => {
                      dispatch(cmd)
                    }}>
                    <span className="cmdk__icon">
                      <CmdkIcon kind={cmd.kind} />
                    </span>
                    <span>{cmd.label}</span>
                    <span className="cmdk__item__hint">{cmd.hint ?? ''}</span>
                  </div>
                )
              })}
            </Fragment>
          ))}
          {flat.length === 0 && <div className="cmdk__empty">no matches.</div>}
        </div>
      </div>
    </div>
  )
}
